import { Popover } from '@headlessui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from '@heroicons/react/20/solid';
import { format, addDays, isBefore, isAfter, startOfDay } from 'date-fns';
import React from 'react';
import { DayPicker } from 'react-day-picker';

import 'react-day-picker/dist/style.css';
import { getTimeInTeamTimezone } from '../../pages/YourTeams/utils/timelineUtils';

import { modifiersStyles } from './constants';
import { createPeriodModifiers, toTeamTimezone } from './helpers';
import { DatePickerProps } from './types';

export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  lastPeriod,
  currentPeriod,
  nextPeriod,
}: DatePickerProps): JSX.Element {
  // Converted to timezone for consistent comparison
  const timezoneValue = React.useMemo(
    () => getTimeInTeamTimezone(value),
    [value],
  );

  const timezoneMinDate = React.useMemo(
    () => toTeamTimezone(minDate),
    [minDate],
  );

  const timezoneMaxDate = React.useMemo(
    () => toTeamTimezone(maxDate),
    [maxDate],
  );

  const modifiers = React.useMemo(() => {
    const mods: { [key: string]: (date: Date) => boolean } = {};

    if (lastPeriod) {
      Object.assign(mods, createPeriodModifiers(lastPeriod, 'lastPeriod'));
    }

    if (currentPeriod) {
      Object.assign(
        mods,
        createPeriodModifiers(currentPeriod, 'currentPeriod'),
      );
    }

    if (nextPeriod) {
      Object.assign(mods, createPeriodModifiers(nextPeriod, 'nextPeriod'));
    }

    return mods;
  }, [lastPeriod, currentPeriod, nextPeriod]);

  const disabledDays = React.useMemo(() => {
    const matchers = [];

    // Always disable weekends
    matchers.push({ dayOfWeek: [0, 6] });

    // Add min date restriction if provided
    if (timezoneMinDate) {
      matchers.push({ before: timezoneMinDate });
    }

    // Add max date restriction if provided
    if (timezoneMaxDate) {
      matchers.push({ after: timezoneMaxDate });
    }

    return matchers;
  }, [timezoneMinDate, timezoneMaxDate]);

  const handlePreviousDay = React.useCallback((): void => {
    let newDate = addDays(value, -1);
    // Skip weekends
    if (value.getDay() === 1) newDate = addDays(value, -3);

    const istNewDate = getTimeInTeamTimezone(newDate);

    // Check if new date is before min date
    if (
      timezoneMinDate &&
      isBefore(startOfDay(istNewDate), startOfDay(timezoneMinDate))
    ) {
      return;
    }

    onChange(istNewDate);
  }, [value, onChange, timezoneMinDate]);

  const handleNextDay = React.useCallback((): void => {
    let newDate = addDays(value, 1);
    // Skip weekends
    if (value.getDay() === 5) newDate = addDays(value, 3);

    const istNewDate = getTimeInTeamTimezone(newDate);

    // Check if new date is after max date
    if (
      timezoneMaxDate &&
      isAfter(startOfDay(istNewDate), startOfDay(timezoneMaxDate))
    ) {
      return;
    }

    onChange(istNewDate);
  }, [value, onChange, timezoneMaxDate]);

  const isPreviousDayDisabled = React.useMemo(() => {
    if (!timezoneMinDate) return false;

    let prevDate = addDays(value, -1);
    if (value.getDay() === 1) prevDate = addDays(value, -3);

    return isBefore(
      startOfDay(getTimeInTeamTimezone(prevDate)),
      startOfDay(timezoneMinDate),
    );
  }, [value, timezoneMinDate]);

  const isNextDayDisabled = React.useMemo(() => {
    if (!timezoneMaxDate) return false;

    let nextDate = addDays(value, 1);
    if (value.getDay() === 5) nextDate = addDays(value, 3);

    return isAfter(
      startOfDay(getTimeInTeamTimezone(nextDate)),
      startOfDay(timezoneMaxDate),
    );
  }, [value, timezoneMaxDate]);

  const handleDaySelect = React.useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(getTimeInTeamTimezone(date));
      }
    },
    [onChange],
  );

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={handlePreviousDay}
        className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
        disabled={isPreviousDayDisabled}
        aria-label="Previous day"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>

      <Popover className="relative">
        {({ close }) => (
          <>
            <Popover.Button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 shadow-sm">
              <span className="text-gray-900 text-sm font-medium">
                {format(timezoneValue, 'EEE, do MMM')}
              </span>
              <CalendarIcon className="w-5 h-5 text-gray-400" />
            </Popover.Button>

            <Popover.Panel className="absolute z-10 mt-2 transform -translate-x-1/2 left-1/2">
              <div className="bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 p-2">
                <DayPicker
                  mode="single"
                  selected={timezoneValue}
                  defaultMonth={timezoneValue}
                  onSelect={(date) => {
                    handleDaySelect(date);
                    close();
                  }}
                  disabled={disabledDays}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                />
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>

      <button
        type="button"
        onClick={handleNextDay}
        className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
        disabled={isNextDayDisabled}
        aria-label="Next day"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  );
}
